import { fetchWithAuth } from "./http";

// --- Types ---

export interface HourlyImpact {
    timestamp: string;
    generation_mwh: number;
    capacity_factor: number;
    grid_carbon_intensity_kg_mwh: number;
    marginal_fuel_type: string;
    displaced_generation: Record<string, number>;
    avoided_co2e_kg: number;
    avoided_emissions: Record<string, number>;
    avoided_dalys: number;
    avoided_pdfs: number;
    economic_value_usd: number;
    data_source: string;
    is_interpolated: boolean;
}

export interface DailyImpact {
    date: string;
    total_generation_mwh: number;
    avg_capacity_factor: number;
    peak_generation_mwh: number;
    avoided_co2e_kg: number;
    avoided_dalys: number;
    avoided_pdfs: number;
    economic_value_usd: number;
    hours_with_data: number;
    hours_estimated: number;
    avg_grid_carbon_intensity: number;
    dominant_marginal_fuel: string;
    displaced_generation: Record<string, number>;
}

export interface ImpactSummary {
    total_generation_mwh: number;
    total_generation_gwh?: number;
    avg_daily_generation_mwh?: number;
    total_avoided_co2e_kg: number;
    total_avoided_co2e_tonnes?: number;
    total_avoided_dalys: number;
    total_avoided_pdfs: number;
    total_economic_value_usd: number;
    hours_count?: number;
    days_count?: number;
}

export interface ImpactResponse<T> {
    project_id: string;
    project_name: string;
    total_records: number;
    returned_records: number;
    offset: number;
    limit: number;
    data: T[];
    summary: ImpactSummary | null;
}

// --- API functions ---

interface ImpactQueryParams {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
}

function buildQueryString(params: ImpactQueryParams): string {
    const qs = new URLSearchParams();
    if (params.startDate) qs.set("start_date", params.startDate);
    if (params.endDate) qs.set("end_date", params.endDate);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    const str = qs.toString();
    return str ? `?${str}` : "";
}

export async function getHourlyImpacts(
    token: string,
    projectId: string,
    params: ImpactQueryParams = {},
): Promise<ImpactResponse<HourlyImpact>> {
    return fetchWithAuth<ImpactResponse<HourlyImpact>>(
        `/projects/${projectId}/impacts/hourly${buildQueryString(params)}`,
        token,
    );
}

export async function getDailyImpacts(
    token: string,
    projectId: string,
    params: ImpactQueryParams = {},
): Promise<ImpactResponse<DailyImpact>> {
    return fetchWithAuth<ImpactResponse<DailyImpact>>(
        `/projects/${projectId}/impacts/daily${buildQueryString(params)}`,
        token,
    );
}
